import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import { lenisScroll } from "@/lib/scroll";
import { IMG, VIDEO_URL } from "@/data/site";

export default function VideoSection() {
  const [open, setOpen] = useState(false);

  return (
    <section id="video" data-testid="video-section" className="relative h-[90vh] min-h-[560px] overflow-hidden">
      <video
        className="absolute inset-0 w-full h-full object-cover"
        poster={IMG.archPool}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
      >
        <source src="/assets/showreel.webm" type="video/webm" />
        <source src={VIDEO_URL} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-ink/60" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />

      <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-center">
        <FadeUp>
          <p className="overline-gold mb-6">Cinematic Post-Production</p>
        </FadeUp>
        <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl leading-[1.0] tracking-tight max-w-4xl">
          <MaskedLines lines={["FROM RAW FOOTAGE", "TO REAL ESTATE CINEMA."]} />
        </h2>
        <FadeUp delay={0.3} className="mt-8 max-w-lg">
          <p className="text-sm md:text-base text-ivory/80 font-light leading-relaxed">
            Every movement, transition and frame is refined to make the property
            feel as extraordinary as it is.
          </p>
        </FadeUp>
        <FadeUp delay={0.45} className="mt-10 flex flex-wrap items-center gap-8">
          <button
            data-testid="video-play-btn"
            data-cursor="PLAY"
            onClick={() => setOpen(true)}
            className="group flex items-center gap-4"
            aria-label="Play showreel"
          >
            <span className="w-16 h-16 rounded-full border border-gold/60 flex items-center justify-center text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-ink">
              <Play size={18} className="ml-0.5" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.3em] text-ivory/80">Watch Showreel</span>
          </button>
          <button
            data-testid="video-portfolio-btn"
            onClick={() => lenisScroll("#portfolio")}
            className="text-[11px] uppercase tracking-[0.3em] text-bronze hover:text-gold transition-colors duration-300"
          >
            View Video Portfolio
          </button>
        </FadeUp>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="video-lightbox"
            className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setOpen(false)}
          >
            <button
              data-testid="video-lightbox-close"
              className="absolute top-6 right-6 text-ivory/70 hover:text-gold transition-colors p-2"
              aria-label="Close video"
            >
              <X size={28} />
            </button>
            <motion.video
              controls
              autoPlay
              className="w-full max-w-5xl border hairline"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <source src="/assets/showreel.webm" type="video/webm" />
              <source src={VIDEO_URL} type="video/mp4" />
            </motion.video>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
